import { memo } from "react";
import { cn } from "@/lib/utils";
import { Seeds } from "./Seed";
import { TESTIDS } from "@/constants/testIds";

/**
 * A single pit on the Tsoro board.
 *
 * props:
 *   row, col          – pit coordinates (0..3, 0..7)
 *   count             – exact counter total (always rendered)
 *   ownerHint         – 'A' | 'B' (for ARIA labels)
 *   isLegal           – whether this is a legal move for the active player
 *   isSelected        – whether currently selected for preview
 *   isLanding         – animate landing highlight
 *   isRelay           – animate relay pulse
 *   isCaptureSource   – about to lose counters (capture highlight)
 *   onSelect          – click/keyboard handler
 *   showHints         – legal pits should glow if true
 *   coordLabel        – e.g. "A4" if coords enabled
 *   disabled          – global block (animation in progress)
 */
export const Pit = memo(function Pit({
  row,
  col,
  count,
  ownerHint,
  isLegal,
  isSelected,
  isLanding,
  isRelay,
  isCaptureSource,
  onSelect,
  showHints,
  coordLabel,
  disabled,
  highContrast,
}) {
  const isInner = row === 1 || row === 2;

  const handleClick = () => {
    if (disabled) return;
    if (onSelect) onSelect(row, col);
  };
  const handleKey = (e) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (onSelect) onSelect(row, col);
    }
  };

  const baseLabel = `${ownerHint === "A" ? "Player 1" : "Player 2"} ${isInner ? "inner" : "outer"} row, pit ${col + 1}, ${count} ${count === 1 ? "counter" : "counters"}${isLegal ? ", legal move" : ""}`;

  return (
    <button
      type="button"
      role="gridcell"
      data-testid={TESTIDS.pit(row, col)}
      data-row={row}
      data-col={col}
      data-count={count}
      data-legal={isLegal ? "true" : "false"}
      aria-label={baseLabel}
      aria-pressed={isSelected ? "true" : "false"}
      disabled={disabled && !isLegal}
      onClick={handleClick}
      onKeyDown={handleKey}
      tabIndex={isLegal ? 0 : -1}
      className={cn(
        "relative aspect-square rounded-full pit-base no-tap-highlight",
        "flex items-center justify-center select-none",
        "transition-[transform,box-shadow,opacity] duration-200",
        isLegal && showHints && !isSelected && "ring-2 ring-[hsl(var(--highlight-legal))] ring-offset-2 ring-offset-transparent",
        isSelected && "scale-[1.05] ring-4 ring-[hsl(var(--primary))]",
        isLanding && "animate-capture-flash",
        isRelay && "animate-relay-pulse",
        isCaptureSource && "ring-4 ring-[hsl(var(--highlight-capture))]",
        !isLegal && !isSelected && "opacity-70",
        disabled && "cursor-not-allowed",
        highContrast && "border-2 border-foreground"
      )}
    >
      <Seeds count={count} row={row} col={col} />

      {/* Numeric counter badge — exact source of truth */}
      <span
        className={cn(
          "absolute -top-1.5 -right-1.5 z-10",
          "min-w-[1.5rem] h-6 px-1.5 rounded-full",
          "bg-background text-foreground border border-border/70",
          "font-mono text-xs font-bold leading-none flex items-center justify-center shadow"
        )}
      >
        {count}
      </span>

      {coordLabel && (
        <span className="absolute bottom-0.5 left-1.5 text-[10px] font-mono text-foreground/60 pointer-events-none">
          {coordLabel}
        </span>
      )}
    </button>
  );
});
